import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Upload as UploadIcon, 
  FileText, 
  X, 
  CheckCircle2,
  RefreshCw,
  Image,
  FileSpreadsheet,
  Presentation,
  ChevronsUpDown,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// All supported languages - sorted by name
const ALL_LANGUAGES = {
  'af': 'Afrikaans',
  'sq': 'Albanian (Shqip)',
  'am': 'Amharic (አማርኛ)',
  'ar': 'Arabic (العربية)',
  'hy': 'Armenian (Հdelays)',
  'az': 'Azerbaijani (Azərbaycan)',
  'eu': 'Basque (Euskara)',
  'bn': 'Bengali (বাংলা)',
  'bs': 'Bosnian (Bosanski)',
  'bg': 'Bulgarian (Български)',
  'my': 'Burmese (မြန်မာ)',
  'ca': 'Catalan (Català)',
  'zh': 'Chinese (中文)',
  'zh-cn': 'Chinese Simplified (简体中文)',
  'zh-tw': 'Chinese Traditional (繁體中文)',
  'hr': 'Croatian (Hrvatski)',
  'cs': 'Czech (Čeština)',
  'da': 'Danish (Dansk)',
  'nl': 'Dutch (Nederlands)',
  'en': 'English',
  'eo': 'Esperanto',
  'et': 'Estonian (Eesti)',
  'fi': 'Finnish (Suomi)',
  'fr': 'French (Français)',
  'gl': 'Galician (Galego)',
  'ka': 'Georgian (ქართული)',
  'de': 'German (Deutsch)',
  'el': 'Greek (Ελληνικά)',
  'gu': 'Gujarati (ગુજરાતી)',
  'ha': 'Hausa (Hausa)',
  'he': 'Hebrew (עברית)',
  'hi': 'Hindi (हिन्दी)',
  'hu': 'Hungarian (Magyar)',
  'is': 'Icelandic (Íslenska)',
  'ig': 'Igbo (Igbo)',
  'id': 'Indonesian (Bahasa Indonesia)',
  'ga': 'Irish (Gaeilge)',
  'it': 'Italian (Italiano)',
  'ja': 'Japanese (日本語)',
  'kn': 'Kannada (ಕನ್ನಡ)',
  'kk': 'Kazakh (Қазақ)',
  'km': 'Khmer (ខ្មែរ)',
  'ko': 'Korean (한국어)',
  'ku': 'Kurdish (Kurdî)',
  'ky': 'Kyrgyz (Кыргызча)',
  'lo': 'Lao (ລາວ)',
  'la': 'Latin (Latina)',
  'lv': 'Latvian (Latviešu)',
  'lt': 'Lithuanian (Lietuvių)',
  'mk': 'Macedonian (Македонски)',
  'ms': 'Malay (Bahasa Melayu)',
  'ml': 'Malayalam (മലയാളം)',
  'mt': 'Maltese (Malti)',
  'mr': 'Marathi (मराठी)',
  'mn': 'Mongolian (Монгол)',
  'ne': 'Nepali (नेपाली)',
  'no': 'Norwegian (Norsk)',
  'ps': 'Pashto (پښتو)',
  'fa': 'Persian/Farsi (فارسی)',
  'pl': 'Polish (Polski)',
  'pt': 'Portuguese (Português)',
  'pa': 'Punjabi (ਪੰਜਾਬੀ)',
  'ro': 'Romanian (Română)',
  'ru': 'Russian (Русский)',
  'sr': 'Serbian (Српски)',
  'si': 'Sinhala (සිංහල)',
  'sk': 'Slovak (Slovenčina)',
  'sl': 'Slovenian (Slovenščina)',
  'so': 'Somali (Soomaali)',
  'es': 'Spanish (Español)',
  'sw': 'Swahili (Kiswahili)',
  'sv': 'Swedish (Svenska)',
  'tl': 'Tagalog/Filipino',
  'tg': 'Tajik (Тоҷикӣ)',
  'ta': 'Tamil (தமிழ்)',
  'te': 'Telugu (తెలుగు)',
  'th': 'Thai (ไทย)',
  'tr': 'Turkish (Türkçe)',
  'uk': 'Ukrainian (Українська)',
  'ur': 'Urdu (اردو)',
  'uz': 'Uzbek (Oʻzbek)',
  'vi': 'Vietnamese (Tiếng Việt)',
  'cy': 'Welsh (Cymraeg)',
  'xh': 'Xhosa (isiXhosa)',
  'yo': 'Yoruba (Yorùbá)',
  'zu': 'Zulu (isiZulu)',
};

// Sort languages by name
const SORTED_LANGUAGES = Object.entries(ALL_LANGUAGES)
  .sort((a, b) => a[1].localeCompare(b[1]))
  .map(([code, name]) => ({ code, name }));

// Supported file types
const SUPPORTED_TYPES = {
  documents: ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt'],
  images: ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.heic'],
  spreadsheets: ['.xls', '.xlsx', '.csv'],
  presentations: ['.ppt', '.pptx'],
};

const ALL_EXTENSIONS = [
  ...SUPPORTED_TYPES.documents,
  ...SUPPORTED_TYPES.images,
  ...SUPPORTED_TYPES.spreadsheets,
  ...SUPPORTED_TYPES.presentations,
];

export default function Upload() {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState('en');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const selectedLanguageName = useMemo(() => {
    return ALL_LANGUAGES[language] || 'Select language...';
  }, [language]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const validateAndSetFile = (selectedFile) => {
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    
    if (!ALL_EXTENSIONS.includes(ext)) {
      toast.error(`File type ${ext} not supported. Supported: ${ALL_EXTENSIONS.join(', ')}`);
      return;
    }
    
    if (selectedFile.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB.');
      return;
    }
    
    setFile(selectedFile);
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('language', language);
      formData.append('metadata', JSON.stringify({}));
      
      const response = await api().post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Document uploaded successfully!');
      navigate(`/documents/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (filename) => {
    if (!filename) return FileText;
    const ext = '.' + filename.split('.').pop().toLowerCase();
    if (SUPPORTED_TYPES.images.includes(ext)) return Image;
    if (SUPPORTED_TYPES.spreadsheets.includes(ext)) return FileSpreadsheet;
    if (SUPPORTED_TYPES.presentations.includes(ext)) return Presentation;
    return FileText;
  };

  const FileIcon = file ? getFileIcon(file.name) : FileText;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="upload-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Upload Document</h1>
        <p className="text-muted-foreground mt-1">Upload documents, images, spreadsheets, or presentations</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Document Details</CardTitle>
            <CardDescription>Supported formats: PDF, Word, Text, Images, Excel, PowerPoint</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                ${file ? 'bg-muted/30' : ''}
              `}
            >
              {file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={removeFile}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept={ALL_EXTENSIONS.join(',')}
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-input"
                  />
                  <UploadIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium mb-1">Drag and drop your file here</p>
                  <p className="text-sm text-muted-foreground">or click to browse</p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    <span className="text-xs bg-muted px-2 py-1 rounded">PDF</span>
                    <span className="text-xs bg-muted px-2 py-1 rounded">DOC/DOCX</span>
                    <span className="text-xs bg-muted px-2 py-1 rounded">Images</span>
                    <span className="text-xs bg-muted px-2 py-1 rounded">Excel</span>
                    <span className="text-xs bg-muted px-2 py-1 rounded">PowerPoint</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Max 100MB</p>
                </>
              )}
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Document Title</Label>
              <Input
                id="title"
                placeholder="Enter document title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="title-input"
              />
            </div>

            {/* Language - Searchable Combobox */}
            <div className="space-y-2">
              <Label>Document Language</Label>
              <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={languageOpen}
                    className="w-full justify-between"
                    data-testid="language-select"
                  >
                    {selectedLanguageName}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search language..." />
                    <CommandList>
                      <CommandEmpty>No language found.</CommandEmpty>
                      <CommandGroup className="max-h-[300px] overflow-auto">
                        {SORTED_LANGUAGES.map((lang) => (
                          <CommandItem
                            key={lang.code}
                            value={lang.name}
                            onSelect={() => {
                              setLanguage(lang.code);
                              setLanguageOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                language === lang.code ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {lang.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Type to search - used for language validation
              </p>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full h-12"
              disabled={!file || uploading}
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </form>

      {/* Supported Types Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Supported File Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="flex items-center gap-2 font-medium mb-2">
                <FileText className="w-4 h-4" />
                Documents
              </div>
              <ul className="text-muted-foreground space-y-1">
                {SUPPORTED_TYPES.documents.map(ext => (
                  <li key={ext}>{ext}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 font-medium mb-2">
                <Image className="w-4 h-4" />
                Images
              </div>
              <ul className="text-muted-foreground space-y-1">
                {SUPPORTED_TYPES.images.slice(0, 5).map(ext => (
                  <li key={ext}>{ext}</li>
                ))}
                <li>+{SUPPORTED_TYPES.images.length - 5} more</li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 font-medium mb-2">
                <FileSpreadsheet className="w-4 h-4" />
                Spreadsheets
              </div>
              <ul className="text-muted-foreground space-y-1">
                {SUPPORTED_TYPES.spreadsheets.map(ext => (
                  <li key={ext}>{ext}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 font-medium mb-2">
                <Presentation className="w-4 h-4" />
                Presentations
              </div>
              <ul className="text-muted-foreground space-y-1">
                {SUPPORTED_TYPES.presentations.map(ext => (
                  <li key={ext}>{ext}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
